/**
 * useConversationTraces.ts — fetch lifecycle for the "Rastros" (traces) tab
 * (Fowler's design doc, "MLflow design", Slice 6). Mirrors
 * `useConversationMessages`'s shape, with one extra phase: 503
 * (`mlflow_not_configured`) is distinguished from a generic 502/transport
 * error — it renders a calm "not configured" state with no retry, per the S6
 * dispatch brief.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { ApiError, UnauthorizedError } from '../../api/client';
import type { TraceItem } from '../../api/types';
import floating from '../../lib/floating';

export type ConversationTracesPhase =
  'loading' | 'ready' | 'error' | 'notConfigured' | 'unauthorized';

export interface ConversationTracesState {
  traces: TraceItem[];
  phase: ConversationTracesPhase;
  refetch: () => void;
}

export const useConversationTraces = (
  apiClient: ApiClient,
  threadId: string,
): ConversationTracesState => {
  const [traces, setTraces] = useState<TraceItem[]>([]);
  const [phase, setPhase] = useState<ConversationTracesPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetch = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const data = await apiClient.getConversationTraces(threadId);
      if (!isCurrent()) return;
      setTraces(data.items);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      if (error instanceof ApiError && error.status === 503) {
        setPhase('notConfigured');
        return;
      }
      // 404 / 502 / transport — same clean error+retry state.
      setPhase('error');
    }
  }, [apiClient, threadId]);

  useEffect(() => {
    floating(fetch());
  }, [fetch]);

  return { traces, phase, refetch: fetch };
};
